import { useTheme } from '../context/ThemeContext';

// Cortex Toggle — an accessible on/off switch (role="switch", aria-checked). The knob slides on
// a gradient track when on; the whole control is a real <button> so it is keyboard operable
// (Space/Enter) with a visible focus ring. Used for binary state like "provider enabled".

export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible label (required — the switch has no visible text of its own). */
  label: string;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
}

export function Toggle({ checked, onChange, label, disabled = false, loading = false }: ToggleProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isDisabled = disabled || loading;

  const trackOn = 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-inner';
  const trackOff = isDark ? 'bg-white/[0.1]' : 'bg-slate-300';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? trackOn : trackOff
      }`}
    >
      <span
        aria-hidden="true"
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        } ${loading ? 'animate-pulse' : ''}`}
      />
    </button>
  );
}
